import React from "react";
import { useNavigate } from "react-router-dom";
import PageRoutes from "../../../utils/constants";
import { CreditCard, ArrowLeft } from "lucide-react";
import { Button } from "../../../v2/components/ui/button";

const NothingToCancel = ({
  payUrl,
  reason,
}: {
  payUrl: string | null;
  reason: "never_started" | "no_subscription";
}) => {
  const isNeverStarted = reason === "never_started";
  const heading = isNeverStarted
    ? "There's nothing to cancel yet"
    : "There's nothing to cancel";
  const body = isNeverStarted
    ? "Your subscription isn't active because the payment hasn't gone through. Finish paying to start watching."
    : "You don't have a subscription to cancel right now.";
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4 w-full">
      <div className="relative group max-w-md w-full mx-auto">
        {/* Subtle glow effect behind the card */}
        <div className="absolute -inset-0.5 bg-gradient-to-br from-slate-200 to-slate-300 rounded-[2rem] blur-lg opacity-30 group-hover:opacity-50 transition duration-700 group-hover:duration-200" />
        
        <div className="relative bg-white border border-slate-100 rounded-[2rem] p-8 sm:p-10 shadow-2xl shadow-slate-200/40 text-center overflow-hidden flex flex-col items-center">
          {/* Decorative background element */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-slate-50 to-white rounded-full opacity-70 pointer-events-none transform translate-x-1/3 -translate-y-1/3" />
          
          <div className="relative z-10 flex flex-col items-center w-full">
            <div className="w-16 h-16 bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-2xl flex items-center justify-center mb-6 shadow-sm transform -rotate-3 hover:rotate-0 transition-transform duration-300">
              <CreditCard className="w-8 h-8 text-slate-500" strokeWidth={1.75} />
            </div>
            
            <h1 className="text-2xl font-extrabold text-slate-900 mb-3 tracking-tight">
              {heading}
            </h1>
            
            <p className="text-[15px] text-slate-500 mb-3 max-w-[280px] mx-auto leading-relaxed">
              {body}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full justify-center items-center">
              {isNeverStarted && payUrl && (
                <Button variant="default" onClick={() => navigate(payUrl)} className="w-full sm:w-auto font-bold rounded-xl h-11 px-6 bg-action hover:bg-action-hover text-white">
                  Complete payment
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate(PageRoutes.AccountPage)} className="w-full sm:w-auto font-bold rounded-xl h-11 px-6 group">
                <ArrowLeft className="w-4 h-4 mr-2 text-slate-400 group-hover:-translate-x-1 transition-transform" />
                Back to My Account
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NothingToCancel;
